import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Eye,
    Pencil,
    Plus,
} from "lucide-react";

import EntityCard from "@/framework/entity/EntityCard";
import EntityTable, {
    EntityColumn,
} from "@/framework/entity/EntityTable";
import Button from "@/framework/buttons/Button";

import { OrderUserService } from "../service/orderUserService";
import { UserOrderResponse } from "@/api";

export default function UserOrderView() {
    const navigate = useNavigate();

    const [orders, setOrders] =
        useState<UserOrderResponse[]>([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState<string | null>(null);

    //--------------------------------------------------
    // Load Orders
    //--------------------------------------------------

    const loadOrders = useCallback(
        async () => {
            setLoading(true);
            setError(null);

            try {
                const result =
                    await OrderUserService.list();

                setOrders(result);
            } catch (err) {
                console.error(
                    "Failed to load orders",
                    err,
                );

                setError(
                    "Unable to load your orders.",
                );
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    //--------------------------------------------------
    // Navigation
    //--------------------------------------------------

    function handleNew() {
        navigate("new");
    }

    function handleInvoice(
        event: React.MouseEvent,
        order: UserOrderResponse,
    ) {
        event.stopPropagation();

        navigate(
            `invoice/${order.id}`,
        );
    }

    //--------------------------------------------------
    // Columns
    //--------------------------------------------------
    const columns: EntityColumn<UserOrderResponse>[] = [
        {
            header: "Order #",

            render: order =>
                `#${order.id}`,

            width: "w-24",
        },

        {
            header: "Order Date",

            render: order =>
                order.order_date
                    ? new Date(
                        order.order_date,
                    ).toLocaleDateString()
                    : "—",
        },

        {
            header: "Items",

            render: order =>
                order.items?.length,

            align: "center",
            width: "w-24",
        },

        {
            header: "Order Status",

            render: order => {
                const status = String(
                    order.order_status ?? "",
                )
                    .trim()
                    .toLowerCase();

                const explanation =
                    status === "created"
                        ? "Invoicing is in progress"
                        : status === "invoiced"
                            ? "Invoice is ready. You can view the invoice and make payment"
                            : null;

                return (
                    <div className="flex flex-col">
                        <span className="text-sm font-semibold uppercase text-slate-800">
                            {status || "—"}
                        </span>

                        {explanation && (
                            <span className="mt-1 text-xs text-slate-500">
                                {explanation}
                            </span>
                        )}
                    </div>
                );
            },
        },


        {
            header: "Actions",

            render: order => {
                const isInvoiced =
                    order.order_status === "invoiced";

                return (
                    <div className="flex justify-end gap-2">
                        {isInvoiced ? (
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={event =>
                                    handleInvoice(
                                        event,
                                        order,
                                    )
                                }
                            >
                                <Eye size={15} />
                                <span>View Invoice</span>
                            </Button>
                        ) : (
                            <span className="text-sm text-slate-400">
                                —
                            </span>
                        )}
                    </div>
                );
            },

            align: "right",
            width: "w-48",
        },
    ];

    //--------------------------------------------------
    // Render
    //--------------------------------------------------

    return (
        <div className="space-y-6">
            <EntityCard
                title="My Orders"
                subtitle="View your orders, invoices and payment status."
                actions={
                    <Button onClick={handleNew}>
                        <Plus size={16} />
                        <span>New</span>
                    </Button>
                }
            >
                {error && (
                    <div
                        className="
                            mb-4
                            rounded-lg
                            border
                            border-red-200
                            bg-red-50
                            px-4
                            py-3
                            text-sm
                            text-red-700
                        "
                    >
                        {error}
                    </div>
                )}

                {loading ? (
                    <div
                        className="
                            py-10
                            text-center
                            text-sm
                            text-slate-500
                        "
                    >
                        Loading your orders...
                    </div>
                ) : (
                    <EntityTable
                        items={orders}
                        columns={columns}
                        getKey={order => order.id}
                        emptyMessage="You don't have any orders yet."
                    // onRowClick={handleOpen}
                    />
                )}
            </EntityCard>
        </div>
    );
}